import { Bell, User, LogOut, Sun, Moon } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { isPast, parseISO } from 'date-fns'

const pageTitles = {
  '/': 'Dashboard',
  '/scope': 'Scope Definition',
  '/processes': 'Process Register',
  '/assets': 'Asset Register',
  '/risks': 'Risk Register',
  '/controls': 'Annex A Controls',
  '/soa': 'Statement of Applicability',
  '/clauses': 'Clause Compliance',
  '/audits': 'Audit Management',
  '/non-conformity': 'Non-Conformity & CAPA',
  '/documents': 'Document Library',
  '/training': 'Training & Awareness',
  '/settings': 'Settings',
  '/recycle-bin': 'Recycle Bin',
  '/audit-logs': 'System Audit Logs',
}

export function Topbar({ user }) {
  const location = useLocation()
  const navigate = useNavigate()
  const title = pageTitles[location.pathname] || 'ISO 27001 Platform'
  const { logout } = useAuth()
  const { isDarkMode, toggleTheme } = useTheme()
  const [showNotifications, setShowNotifications] = useState(false)
  const notifRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // For Contributor/User use myWork, for Admin fetch everything
  const isAdmin = user?.role === 'Admin'
  const { data: myWork } = useQuery({ queryKey: ['my-work'], queryFn: () => api.getMyWork(), enabled: !isAdmin })
  const { data: allRisks = [] } = useQuery({ queryKey: ['risks'], queryFn: api.getRisks, enabled: isAdmin })
  const { data: allControls = [] } = useQuery({ queryKey: ['security-controls'], queryFn: api.getSecurityControls, enabled: isAdmin })
  const { data: allVulns = [] } = useQuery({ queryKey: ['vulnerabilities'], queryFn: api.getVulnerabilities, enabled: isAdmin })
  const { data: allDocs = [] } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments, enabled: isAdmin })

  const notifications = []
  
  const isOverdue = (dateStr) => {
    if (!dateStr) return false
    try { const d = parseISO(dateStr); return !isNaN(d.getTime()) && isPast(d) }
    catch { return false }
  }

  const risks = isAdmin ? allRisks : (myWork?.risks || [])
  const controls = isAdmin ? allControls : (myWork?.controls || [])
  const vulns = isAdmin ? allVulns : (myWork?.vulnerabilities || [])
  const docs = isAdmin ? allDocs.filter(d => d.status === 'Under Review') : (myWork?.documents || [])

  risks.forEach(r => {
    if (['Open', 'In Progress'].includes(r.status)) {
      if (isOverdue(r.due_date)) notifications.push({ id: `r-${r.id}`, type: 'Risk', text: `Overdue Risk: ${r.custom_id || 'RSK'}`, color: 'var(--danger)', link: '/risks' })
    }
  })
  
  controls.forEach(c => {
    if (['Not Started', 'In Progress', 'Planned'].includes(c.implementation_status)) {
      if (isOverdue(c.due_date)) notifications.push({ id: `c-${c.id}`, type: 'Control', text: `Overdue Control: ${c.control_id || 'CTRL'}`, color: 'var(--danger)', link: '/controls-library' })
    }
  })
  
  vulns.forEach(v => {
    if (['Open', 'In Progress'].includes(v.status)) {
      if (isOverdue(v.due_date)) notifications.push({ id: `v-${v.id}`, type: 'Vulnerability', text: `Overdue Vuln: ${v.custom_id || 'VULN'}`, color: 'var(--danger)', link: '/vulnerabilities' })
    }
  })
  
  docs.forEach(d => {
    notifications.push({ id: `d-${d.id}`, type: 'Document', text: `Pending Review: ${d.title}`, color: 'var(--warning)', link: '/review-center' })
  })

  return (
    <header className="topbar">
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          {title}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {user?.full_name} <span style={{ fontWeight: 400, opacity: 0.8 }}>({(user?.role || '').toLowerCase()})</span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button 
            className="btn btn-ghost btn-sm" 
            style={{ padding: '7px' }}
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <div style={{ position: 'relative' }} ref={notifRef}>
            <button className="btn btn-ghost btn-sm" style={{ padding: '7px', position: 'relative' }} onClick={() => setShowNotifications(!showNotifications)}>
              <Bell size={16} />
              {notifications.length > 0 && (
                <span style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, background: 'var(--danger)', borderRadius: '50%' }} />
              )}
            </button>

            {showNotifications && (
              <div style={{ 
                position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 280, 
                background: 'var(--bg-elevated)', border: '1px solid var(--border)', 
                borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100,
                maxHeight: 350, overflowY: 'auto'
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)', fontWeight: 600, fontSize: '0.875rem' }}>
                  Notifications ({notifications.length})
                </div>
                {notifications.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    You have no new notifications.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {notifications.map(n => (
                      <div 
                        key={n.id} 
                        onClick={() => { setShowNotifications(false); navigate(n.link); }}
                        style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-light)', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: 10 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.color }} />
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={n.text}>
                          {n.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div 
          onClick={logout}
          title="Sign Out"
          style={{
            width: 36, height: 36, borderRadius: '10px',
            background: 'var(--surface-hover)', border: '1px solid var(--border-color)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 0.2s'
          }}
        >
          <LogOut size={16} color="var(--danger)" />
        </div>
      </div>
    </header>
  )
}
